import { useEffect, useState, useRef } from 'react';
import React from 'react';

/**
 * A utility component that preloads essential data files for d3-celestial
 * This helps ensure the data is available when needed for the star map
 */
const CelestialDataLoader = ({ onLoadComplete, onLoadError, onProgressUpdate }) => {
    const [loadingStatus, setLoadingStatus] = useState({
        loading: true,
        error: false,
        progress: 0
    });

    // Use ref to track loading status without triggering re-renders
    const loadingRef = useRef({
        isLoading: true,
        isPreloaded: false,
        hasRun: false
    });

    useEffect(() => {
        // Skip if this effect has already run
        if (loadingRef.current.hasRun) {
            return;
        }

        // Mark that we've started the loading process
        loadingRef.current.hasRun = true;

        // Skip if data was already loaded
        if (window.celestialDataPreloaded === true) {
            console.log('Celestial data already preloaded, skipping loader');
            setLoadingStatus({
                loading: false,
                error: false,
                progress: 100
            });
            if (onProgressUpdate) onProgressUpdate(100);
            if (onLoadComplete) onLoadComplete();
            return;
        }

        // Only essential files to keep loading time reasonable
        const essentialFiles = [
            'stars.6.json',           // Basic star data
            'constellations.json',    // Constellation data
            'constellations.lines.json', // Constellation lines
            'dsos.bright.json'        // Bright deep sky objects
        ];

        // Get the data path from the global setting if available
        // Try multiple fallback approaches
        const basePath = window.location.origin;
        const relativePath = '/d3-celestial-data/';
        const dataPath = `${basePath}${relativePath}`;

        console.log(`CelestialDataLoader using absolute path: ${dataPath}`);
        console.log(`Also trying relative path: ${relativePath} as fallback`);

        let filesLoaded = 0;
        let errors = 0;
        let errorDetails = [];

        // Function to fetch a single file with fallbacks
        const fetchFile = async (filename) => {
            try {
                // Try absolute URL first
                const fileUrl = `${dataPath}${filename}`;
                console.log(`Fetching celestial data file: ${fileUrl}`);

                let response = await fetch(fileUrl);

                // If that fails, try the relative path
                if (!response.ok) {
                    console.warn(`Failed to load from absolute path, trying relative path: ${relativePath}${filename}`);
                    response = await fetch(`${relativePath}${filename}`);

                    if (!response.ok) {
                        const errorMsg = `Failed to load ${filename} from all paths`;
                        console.error(errorMsg);
                        errorDetails.push(errorMsg);
                        throw new Error(errorMsg);
                    }
                }

                // We need to check if the response is valid JSON
                const jsonData = await response.json();

                // Log success
                console.log(`Successfully loaded ${filename}`);
                return { success: true, filename };
            } catch (error) {
                console.error(`Error loading celestial data file: ${filename}`, error);
                errorDetails.push(`Error loading ${filename}: ${error.message}`);
                return { success: false, filename, error };
            }
        };

        // Load all files in parallel
        const loadAllFiles = async () => {
            console.log(`Starting to load ${essentialFiles.length} celestial data files...`);

            const results = await Promise.all(essentialFiles.map(file =>
                fetchFile(file)
                    .then(result => {
                        filesLoaded++;
                        const progress = Math.round((filesLoaded / essentialFiles.length) * 100);

                        // Only update state if we're still loading (prevent updates after unmount)
                        if (loadingRef.current.isLoading) {
                            setLoadingStatus(prev => ({
                                ...prev,
                                progress
                            }));

                            // Notify parent component of progress
                            if (onProgressUpdate) {
                                onProgressUpdate(progress);
                            }
                        }

                        return result;
                    })
                    .catch(error => {
                        errors++;
                        errorDetails.push(`Unhandled error for file: ${error.message || 'Unknown error'}`);
                        return { success: false, error };
                    })
            ));

            // Prevent further processing if component is unmounted
            if (!loadingRef.current.isLoading) return;

            // Log the results for debugging
            console.log('Celestial data loading results:', {
                total: essentialFiles.length,
                loaded: filesLoaded,
                failed: errors,
                results: results.map(r => ({ file: r.filename, success: r.success }))
            });

            // Check if all essential files were loaded
            const allLoaded = results.every(result => result.success);

            if (allLoaded) {
                console.log('All celestial data files preloaded successfully');
                // Mark data as preloaded in window object to avoid duplicate loading
                window.celestialDataPreloaded = true;
                loadingRef.current.isPreloaded = true;

                // Update loading status state
                setLoadingStatus({
                    loading: false,
                    error: false,
                    progress: 100
                });

                // Notify parent component of final progress
                if (onProgressUpdate) {
                    onProgressUpdate(100);
                }

                // Dispatch a custom event to notify that data is loaded
                window.dispatchEvent(new CustomEvent('celestial-data-loaded'));

                // Delay callbacks slightly to ensure state updates are processed
                setTimeout(() => {
                    // Notify parent component
                    if (onLoadComplete) {
                        onLoadComplete();
                        console.log('onLoadComplete callback fired');
                    }

                    // Trigger celestial initialization after data loaded
                    if (window.celestialLoaded && window.Celestial &&
                        (!window.Celestial.settings || !window.Celestial.settings.datapath)) {
                        console.log('Triggering Celestial initialization after data load');
                        if (typeof window.initCelestial === 'function') {
                            window.initCelestial();
                        }
                    }
                }, 300);
            } else {
                console.error('Failed to load some celestial data files', {
                    errors: errorDetails,
                    failedFiles: results.filter(r => !r.success).map(r => r.filename)
                });

                setLoadingStatus({
                    loading: false,
                    error: true,
                    progress: Math.round((filesLoaded / essentialFiles.length) * 100)
                });

                // Notify parent component
                if (onLoadError) onLoadError(errors, errorDetails.join('; '));
            }
        };

        loadAllFiles();

        // Cleanup function
        return () => {
            loadingRef.current.isLoading = false;
            // Ensure loading is complete when component unmounts
            if (onProgressUpdate && loadingStatus.progress === 100) {
                onProgressUpdate(100);
            }
        };
    }, [onLoadComplete, onLoadError, onProgressUpdate, loadingStatus.progress]);

    // This component doesn't render anything
    return null;
};

// Export with memo to prevent unnecessary re-renders
export default React.memo(CelestialDataLoader);